import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Dialog, IconButton, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { OBSIDIAN, OBSIDIAN_BODY_FONT, OBSIDIAN_DISPLAY_FONT } from '../../constants/obsidianDashboardTheme';
import { showToastMessage, useDispatchPromise } from '../../utils/AppUtils';
import { type HostOfficeHoursBooking } from '../../utils/sandboxOfficeHoursApi';
import { cancelMyHostOfficeHourBooking, fetchMyHostOfficeHourBookings } from '../../redux/sandbox/actions';

type Props = { active: boolean };

function asList(res: unknown): HostOfficeHoursBooking[] {
    if (Array.isArray(res)) return res;
    const data = (res as { data?: HostOfficeHoursBooking[] })?.data;
    return Array.isArray(data) ? data : [];
}

function formatWhen(iso: string) {
    try {
        return new Intl.DateTimeFormat(undefined, {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        }).format(new Date(iso));
    } catch {
        return iso;
    }
}

function studentName(b: HostOfficeHoursBooking) {
    const u = b.booker_user;
    return (
        [u?.firstName, u?.lastName].filter(Boolean).join(' ') ||
        u?.userName ||
        b.booker_name ||
        b.booker_email ||
        'Student'
    );
}

function canCancel(b: HostOfficeHoursBooking) {
    return (
        (b.status === 'confirmed' || b.status === 'pending_payment') &&
        b.payment?.status !== 'refunded' &&
        new Date(b.start_time).getTime() >= Date.now() - 60 * 60 * 1000
    );
}

/** Instructor: upcoming student office-hour bookings with notes + project. */
export function SandboxOfficeHoursHostBookingsPanel({ active }: Props) {
    const dispatchPromise = useDispatchPromise();
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [rows, setRows] = useState<HostOfficeHoursBooking[]>([]);
    const [cancelling, setCancelling] = useState<HostOfficeHoursBooking | null>(null);
    const [reason, setReason] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            setRows(asList(await dispatchPromise(fetchMyHostOfficeHourBookings())));
        } catch (e: any) {
            showToastMessage(e?.message || 'Could not load bookings', 'error');
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [dispatchPromise]);

    useEffect(() => {
        if (active) void load();
    }, [active, load]);

    const submitCancel = async () => {
        if (!cancelling) return;
        const trimmed = reason.trim();
        if (!trimmed) {
            showToastMessage('Add a reason for the student', 'info');
            return;
        }
        setBusy(true);
        try {
            await dispatchPromise(cancelMyHostOfficeHourBooking({ bookingId: cancelling._id, reason: trimmed }));
            const paid = cancelling.payment?.status === 'paid';
            showToastMessage(paid ? 'Session cancelled — student refunded' : 'Session cancelled', 'success');
            setCancelling(null);
            setReason('');
            await load();
        } catch (e: any) {
            showToastMessage(e?.message || 'Could not cancel session', 'error');
        } finally {
            setBusy(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress sx={{ color: OBSIDIAN.primary }} />
            </Box>
        );
    }

    const upcoming = rows.filter(
        b =>
            (b.status === 'confirmed' || b.status === 'pending_payment') &&
            new Date(b.start_time).getTime() >= Date.now() - 60 * 1000,
    );
    const past = rows.filter(
        b => b.status === 'cancelled' || b.status === 'completed' || new Date(b.start_time).getTime() < Date.now(),
    );

    if (!rows.length) {
        return (
            <Typography sx={{ color: OBSIDIAN.onSurfaceVariant, fontFamily: OBSIDIAN_BODY_FONT }}>
                No student sessions yet. They book from courses where office hours are enabled.
            </Typography>
        );
    }

    const renderRow = (b: HostOfficeHoursBooking) => {
        const projectLabel = b.project_title || b.project?.title;
        return (
            <Box
                key={b._id}
                sx={{
                    p: 1.5,
                    mb: 1,
                    borderRadius: 2,
                    bgcolor: OBSIDIAN.surfaceContainer,
                }}>
                <Typography
                    sx={{
                        fontFamily: OBSIDIAN_DISPLAY_FONT,
                        fontWeight: 700,
                        fontSize: 13,
                        color: OBSIDIAN.onSurface,
                    }}>
                    {studentName(b)}
                </Typography>
                <Typography sx={{ fontSize: 12, color: OBSIDIAN.onSurfaceVariant, fontFamily: OBSIDIAN_BODY_FONT }}>
                    {formatWhen(b.start_time)}
                    {b.status !== 'confirmed' ? ` · ${b.status.replace('_', ' ')}` : ''}
                    {b.cancellation_reason ? ` · ${b.cancellation_reason}` : ''}
                </Typography>
                {projectLabel ? (
                    <Typography sx={{ mt: 0.75, fontSize: 12, color: OBSIDIAN.primary, fontWeight: 700 }}>
                        Project: {projectLabel}
                    </Typography>
                ) : null}
                {b.notes ? (
                    <Typography
                        sx={{
                            mt: 0.5,
                            fontSize: 12,
                            color: OBSIDIAN.onSurface,
                            fontFamily: OBSIDIAN_BODY_FONT,
                            whiteSpace: 'pre-wrap',
                        }}>
                        Notes: {b.notes}
                    </Typography>
                ) : null}
                <Box sx={{ mt: 0.75, display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                    {b.meet_link && b.status === 'confirmed' ? (
                        <Button
                            href={b.meet_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                px: 0,
                                minWidth: 0,
                                textTransform: 'none',
                                color: OBSIDIAN.secondary,
                                fontWeight: 700,
                            }}>
                            Join Meet
                        </Button>
                    ) : null}
                    {canCancel(b) ? (
                        <Button
                            disabled={busy}
                            onClick={() => {
                                setReason('');
                                setCancelling(b);
                            }}
                            sx={{
                                px: 0,
                                minWidth: 0,
                                textTransform: 'none',
                                color: OBSIDIAN.onSurfaceVariant,
                                fontWeight: 700,
                                '&:hover': { color: '#ff7351' },
                            }}>
                            Cancel
                        </Button>
                    ) : null}
                </Box>
            </Box>
        );
    };

    return (
        <Box>
            <Typography
                sx={{
                    mb: 1,
                    fontSize: 11,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: OBSIDIAN.onSurfaceVariant,
                }}>
                Upcoming
            </Typography>
            {upcoming.length ? (
                upcoming.map(renderRow)
            ) : (
                <Typography sx={{ mb: 2, fontSize: 13, color: OBSIDIAN.onSurfaceVariant }}>None upcoming.</Typography>
            )}
            {past.length ? (
                <>
                    <Typography
                        sx={{
                            mb: 1,
                            mt: 2,
                            fontSize: 11,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            color: OBSIDIAN.onSurfaceVariant,
                        }}>
                        Past
                    </Typography>
                    {past.slice(0, 12).map(renderRow)}
                </>
            ) : null}

            <Dialog
                open={Boolean(cancelling)}
                onClose={busy ? undefined : () => setCancelling(null)}
                fullWidth
                maxWidth="xs"
                PaperProps={{
                    sx: {
                        bgcolor: OBSIDIAN.surfaceContainer,
                        backgroundImage: 'none',
                        borderRadius: 3,
                        border: `1px solid ${OBSIDIAN.outline}33`,
                    },
                }}>
                <Box sx={{ p: 2.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                        <Box>
                            <Typography
                                sx={{
                                    fontFamily: OBSIDIAN_DISPLAY_FONT,
                                    fontWeight: 800,
                                    fontSize: 16,
                                    color: OBSIDIAN.onSurface,
                                }}>
                                Cancel session
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: OBSIDIAN.onSurfaceVariant, mt: 0.35 }}>
                                {cancelling ? `${studentName(cancelling)} · ${formatWhen(cancelling.start_time)}` : ''}
                            </Typography>
                        </Box>
                        <IconButton
                            size="small"
                            disabled={busy}
                            onClick={() => setCancelling(null)}
                            sx={{ color: OBSIDIAN.onSurfaceVariant }}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                    <Typography
                        sx={{
                            fontSize: 12,
                            color: OBSIDIAN.onSurfaceVariant,
                            mb: 1.5,
                            fontFamily: OBSIDIAN_BODY_FONT,
                        }}>
                        {cancelling?.payment?.status === 'paid'
                            ? 'The student will get a full refund and an email with your reason.'
                            : 'The student will get an email with your reason.'}
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        label="Reason"
                        value={reason}
                        onChange={e => setReason(e.target.value.slice(0, 500))}
                        sx={{
                            mb: 2,
                            '& .MuiOutlinedInput-root': {
                                bgcolor: OBSIDIAN.surfaceContainerHigh,
                                borderRadius: 2,
                                color: OBSIDIAN.onSurface,
                                fontFamily: OBSIDIAN_BODY_FONT,
                                '& fieldset': { borderColor: `${OBSIDIAN.outline}55` },
                            },
                            '& .MuiInputLabel-root': { color: OBSIDIAN.onSurfaceVariant },
                        }}
                    />
                    <Button
                        fullWidth
                        disabled={busy || !reason.trim()}
                        onClick={() => void submitCancel()}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            bgcolor: '#ff7351',
                            color: '#fff',
                            borderRadius: 999,
                            py: 1.1,
                            '&:hover': { filter: 'brightness(1.05)' },
                            '&.Mui-disabled': {
                                bgcolor: OBSIDIAN.surfaceContainerHighest,
                                color: OBSIDIAN.onSurfaceVariant,
                            },
                        }}>
                        {busy ? 'Cancelling…' : 'Cancel & notify student'}
                    </Button>
                </Box>
            </Dialog>
        </Box>
    );
}
